<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" @close="emit('close')">
    <template #content="{ closeModal }">
      <!-- Pick summary form -->
      <div
        :class="{
          'pick-summary-form': true,
          'pick-summary-form--disabled': isSaving,
        }"
      >
        <div class="pick-summary-form__header">
          <h1 class="global-h1 pick-summary-form__title">Pick an Album to use</h1>
          <!-- / Modal title -->
        </div>

        <!-- Filters panel -->
        <div v-if="!isLoading && allContextTypes.length > 1" class="filters-panel pick-summary-form__filters-panel">
          <SrpCheckbox class="filters-panel__checkbox" v-for="type in allContextTypes" :key="type" size="big">
            <template #input>
              <input type="checkbox" :name="type" :value="type" v-model="selectedTypes" />
            </template>
            <template #text>{{ type }}</template>
          </SrpCheckbox>
        </div>
        <!-- / Filters panel -->

        <LoaderNew v-if="isLoading" style="width: 50px; height: 50px; margin: 0 auto" :borderWidth="3" />

        <div v-else-if="!isLoading && !allSummariesGroupedAndFiltered?.length" style="margin-bottom: 30px; text-align: center">No matches found</div>

        <ul v-else-if="!isLoading && allSummariesGroupedAndFiltered?.length" class="pick-summary-form__sections-list">
          <!-- Summaries section -->
          <li class="summaries-section pick-summary-form__summaries-section" v-for="group in allSummariesGroupedAndFiltered" :key="group.contextType">
            <!-- Section title -->
            <div class="section-title summaries-section__section-title">
              <h5 class="global-h5 section-title__text">
                {{ lodashStartCase(group.contextType) }}
              </h5>
            </div>
            <!-- / Section title -->

            <ul class="summaries-section__summaries-list">
              <!-- Summary container -->
              <li class="summary-container summaries-section__summary-container" v-for="summary in group.summaries" :key="summary.id">
                <ContentSummary
                  class="summary-container__summary"
                  :contentSummaryData="summary"
                  :isUnbindButtonVisible="false"
                  :isDragged="false"
                  :customerId="props.customerId"
                  :isContentSourcedFromLabelVisible="false"
                  @clickViewPhotos="
                    () => {
                      viewAllPhotosSummaryId = summary.id;
                      isViewAllPhotosModalVisible = true;
                    }
                  "
                />

                <SrpButton
                  class="summary-container__pick-button"
                  @click="
                    async () => {
                      await save(props.itemId, summary);
                      closeModal();
                    }
                  "
                >
                  <template v-if="isSaving && summaryIdBeingSaved === summary.id" #icon><LoadingIcon :size="20" /></template>
                  Pick
                </SrpButton>
              </li>
              <!-- / Summary container -->
            </ul>
          </li>
          <!-- / Summaries section -->
        </ul>
      </div>
      <!-- / Pick summary form -->

      <ViewAllPhotosModal v-if="isViewAllPhotosModalVisible" v-model:isVisible="isViewAllPhotosModalVisible" :customerId="props.customerId" :contentSummaryId="viewAllPhotosSummaryId" :zIndex="82" />
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import ContentSummaryRepo from "@repos/ContentSummaryRepo";
import lodashStartCase from "lodash-es/startCase";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import ContentSummary from "@views/Community/ContentCalendar/eventSnippets/ContentSummary.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpCheckbox from "@components/ui/SrpCheckbox.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";

// Types
import { ContentCalendarItem, ContentSummaryContextType, ContentSummaryData } from "@contracts/contentCalendar";
import ViewAllPhotosModal from "@views/Community/ContentCalendar/steps/modals/ViewAllPhotosModal.vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    calendarItem: ContentCalendarItem | null;
    customerId: string;
  }>(),
  {}
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "close"): void;
  (e: "saved", updatedCalendarItem: ContentCalendarItem, contentSummary: ContentSummaryData): void;
}>();

// Load all summaries =========================================================
const allSummaries = ref<Array<ContentSummaryData>>([]);
const isLoading = ref<boolean>(false);

onMounted(async () => {
  isLoading.value = true;

  const response = await ContentSummaryRepo.getAllSummaries(props.customerId);
  allSummaries.value = response.content;

  isLoading.value = false;
});

interface SummariesGroup {
  contextType: ContentSummaryContextType;
  summaries: Array<ContentSummaryData>;
}

const allSummariesGroupedAndFiltered = computed<Array<SummariesGroup>>(() => {
  const allGroups: Array<SummariesGroup> = [];

  allSummaries.value.forEach(summary => {
    if (!selectedTypes.value.includes(summary.contextType)) {
      return;
    }
    // check if allGroups includes the current context type
    let groupIndex = allGroups.findIndex(g => g.contextType === summary.contextType);

    if (groupIndex === -1) {
      // create the group if it doesn't exist
      allGroups.push({ contextType: summary.contextType, summaries: [] });
      groupIndex = allGroups.length - 1;
    }

    // apply filter
    allGroups[groupIndex].summaries.push(summary);
  });

  return allGroups;
});

// Defines filters ============================================================
const selectedTypes = ref<Array<ContentSummaryContextType>>([]);

const allContextTypes = computed<Array<ContentSummaryContextType>>(() => {
  return [...new Set(allSummaries.value.map(s => s.contextType))];
});

watch(allContextTypes, () => (selectedTypes.value = [...allContextTypes.value]));

// Save =======================================================================
const isSaving = ref<boolean>(false);
const summaryIdBeingSaved = ref<string>("");

async function save(itemId: string, summary: ContentSummaryData) {
  summaryIdBeingSaved.value = summary.id;
  isSaving.value = true;

  const updatedItem = await ContentCalendarRepo.updateItem(props.customerId, { ...props.calendarItem, contentSourceId: summary.id, contentSource: summary.contextType });

  emit("saved", updatedItem, summary);

  summaryIdBeingSaved.value = "";
  isSaving.value = false;
}

// Toggle the "View All Photos" modal =========================================
const isViewAllPhotosModalVisible = ref<boolean>(false);
const viewAllPhotosSummaryId = ref<string>("");
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Filters panel ==============================================================
.filters-panel {
  gap: 9px;
  display: flex;
  flex-wrap: wrap;

  &__checkbox {
  }
}

// Section title ==============================================================
.section-title {
  gap: 15px;
  display: flex;

  &__text {
  }

  &::after {
    content: "";
    flex-grow: 1;
    height: 11px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }
}

// Summary container ==========================================================
.summary-container {
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  z-index: 0;
  background: rgba(0, 0, 0, 0.05);

  &__summary {
    width: 100%;
    padding: 0 !important;

    &::before {
      display: none !important;
    }

    :deep(.content-summary-snippet__title) {
      inset: -11px auto auto 11px;
    }
  }

  &__pick-button {
    margin: 15px 15px 0 0;
  }
}

// Summaries section ==========================================================
.summaries-section {
  &__section-title {
    margin-bottom: 25px;
  }

  &__summaries-list {
    gap: $desktop-wide-grid-gap-width;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    list-style: none;
  }

  &__summary-container {
    width: 100%;
  }
}

// Pick summary form ==========================================================
.pick-summary-form {
  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__header {
    margin-bottom: 30px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__title {
    padding-bottom: 20px;
  }

  &__filters-panel {
    margin-bottom: 25px;
  }

  &__sections-list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__summaries-section {
    margin-bottom: 40px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
